import { useTranslation } from "react-i18next";
import { Input } from "@/shared/components/ui/input";
import { IntegrationModelsEditor } from "./IntegrationModelsEditor";
import type { ModelEntry } from "@/features/Integrations/utils/modelEntries";
import type { MetadataFieldDefinition } from "../types/integration";

/**
 * Os campos de `metadata` que o TEMPLATE declara (AB#85605).
 *
 * Mesmo contrato de `settings.form_fields`: o cadastro diz o que a tela deve oferecer, e a tela
 * não sabe nada sobre provedor nenhum. Hoje o tipo em uso é `models` — a lista de modelos, que
 * no Azure OpenAI precisa ser declarada por quem instala, porque a chave do catálogo é o nome
 * do deployment escolhido no recurso do cliente. Declarar um campo de texto para outro provedor
 * amanhã não exige código: ele aparece aqui e é persistido em `metadata[key]`.
 */

/** Rótulo/descrição podem vir traduzidos no cadastro. */
function traduzir(
  valor: MetadataFieldDefinition["label"],
  idioma: string
): string | undefined {
  if (!valor) return undefined;
  if (typeof valor === "string") return valor;
  const curto = idioma.split("-")[0];
  return valor[curto] ?? valor.en ?? valor.pt;
}

export function IntegrationMetadataFields({
  fields,
  values,
  onValueChange,
  modelEntries,
  onModelEntriesChange,
}: {
  fields: MetadataFieldDefinition[];
  values: Record<string, unknown>;
  onValueChange: (key: string, value: unknown) => void;
  modelEntries: ModelEntry[];
  onModelEntriesChange: (entries: ModelEntry[]) => void;
}) {
  const { t, i18n } = useTranslation();

  return (
    <div className="flex flex-col gap-4">
      {fields.map((field) => {
        const rotulo = traduzir(field.label, i18n.language) ?? field.key;
        const descricao = traduzir(field.description, i18n.language);

        if (field.type === "models") {
          return (
            <div key={field.key} className="flex flex-col gap-2">
              <div>
                <label className="text-sm font-medium">
                  {rotulo}
                  {field.required ? <span className="ml-1 text-destructive">*</span> : null}
                </label>
                {descricao ? (
                  <p className="text-xs text-muted-foreground">{descricao}</p>
                ) : null}
              </div>
              <IntegrationModelsEditor
                entries={modelEntries}
                onChange={onModelEntriesChange}
              />
            </div>
          );
        }

        return (
          <div key={field.key} className="space-y-2">
            <label className="text-sm font-medium">
              {rotulo}
              {field.required ? <span className="ml-1 text-destructive">*</span> : null}
            </label>
            {descricao ? <p className="text-xs text-muted-foreground">{descricao}</p> : null}
            <Input
              value={String(values[field.key] ?? "")}
              onChange={(e) => onValueChange(field.key, e.target.value)}
              placeholder={t("integrations.metadata.valuePlaceholder")}
            />
          </div>
        );
      })}
    </div>
  );
}

/** O que impede conectar: campo declarado como obrigatório e vazio. */
export function missingRequiredMetadata(
  fields: MetadataFieldDefinition[],
  values: Record<string, unknown>,
  modelEntries: ModelEntry[]
): MetadataFieldDefinition[] {
  return fields.filter((field) => {
    if (!field.required) return false;
    if (field.type === "models") return modelEntries.length === 0;
    const valor = values[field.key];
    return valor === undefined || valor === null || String(valor).trim() === "";
  });
}
